import { CheckIn, CreateCheckinRequest, CreateCheckinResponse, GetCheckinResponse, Location } from '../../types';
import { del, get, logWithAmplitude, post, throwHttpError } from './api';

import format from 'date-fns/format';
import { getMainPage } from '../page-name';
import translationData from '../state/translation-data';

export async function createCheckin(
    amplitude_callsite: string,
    checkin: CreateCheckinRequest,
    additional_logged_parameters: Object = {}
): Promise<CreateCheckinResponse> {
    getMainPage().errorToastElement?.showToast(await translationData.translatePhrase('Checking in... This may take a few seconds.'));
    const checkin_response = await addCheckinInner(checkin);
    logWithAmplitude(
        amplitude_callsite,
        'Checkin Created',
        Object.assign(
            {},
            { checkin_id: checkin_response._id },
            additional_logged_parameters
        )
    );
    return checkin_response;
}

async function addCheckinInner(checkin: CreateCheckinRequest) {
    const response = await fetch(`/api/checkins`, post(checkin));
    if (!response.ok) {
        await throwHttpError('An error occurred while creating the checkin.', response);
    }

    return await (response.json() as Promise<CreateCheckinResponse>);
}

export async function getCheckins(amplitude_callsite: string) {
    const response = await fetch(`/api/checkins`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the checkins.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get All Checkins for account');
    return await (response.json() as Promise<GetCheckinResponse[]>);
}

export async function getCheckin(
    amplitude_callsite: string,
    checkin_id: string,
    use_cache?: boolean
) {
    const response = await fetch(`/api/checkins/${checkin_id}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the checkin.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Checkin', {
        checkin_id,
        use_cache,
    });

    const normalizedResponse = await response.json() as GetCheckinResponse;

    return normalizedResponse;
}

export async function removeCheckin(amplitude_callsite: string, checkin_id: string) {
    const response = await fetch(`/api/checkins/${checkin_id}`, del());

    if (!response.ok) {
        await throwHttpError('An error occurred while deleting the checkin.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Delete Checkin', {
        checkin_id,
    });
}

export function getCheckinKey(location: Location) {
    // Key by coordinates, not place_id: two points can share a place_id (same address
    // search, one dragged), and the server credits check-ins by exact latLng.
    return location.latLng.lat.toString() + ', ' + location.latLng.lng.toString();
}

export function getCheckinMapFromCheckins(checkins?: GetCheckinResponse[] | CheckIn[], limitToHour?: boolean): Map<String, CheckIn[]> {
    if (!checkins || checkins.length === 0) {
        return new Map<String, CheckIn[]>();
    }

    limitToHour = limitToHour ?? true;

    // Sort by timestamp directly (most recent first)
    checkins.sort((a, b) => {
        const dateA = a.timestamp ?? '';
        const dateB = b.timestamp ?? '';
        return dateB.localeCompare(dateA);
    });

    const checkinMap = new Map<String, CheckIn[]>();

    // Keep track of the hour of the most recent check-in at each location
    const locationLastHourMap = new Map<String, string>();

    for (const checkin of checkins) {
        const key = getCheckinKey(checkin.location);

        // Initialize the array for this location if needed
        if (!checkinMap.has(key)) {
            checkinMap.set(key, []);
        }

        // Create the check-in object to store
        const checkinObj = {
            topic_id: checkin.topic_id,
            location: checkin.location,
            _id: checkin._id,
            timestamp: checkin.timestamp
        };

        // If we're limiting to one check-in per hour
        if (limitToHour && checkin.timestamp) {
            // Get the hour part of the timestamp (YYYY-MM-DDThh)
            const hourString = checkin.timestamp.substring(0, 13);

            // If we've already seen a check-in at this location in this hour, skip it
            if (locationLastHourMap.has(key) && locationLastHourMap.get(key) === hourString) {
                continue; // Skip this check-in
            }

            // Otherwise, update the last hour for this location
            locationLastHourMap.set(key, hourString);
        }

        // Add the check-in to the map
        checkinMap.get(key)?.push(checkinObj);
    }

    return checkinMap;
}

export function getCheckinTimesString(checkins?: CheckIn[]): string {
    if (!checkins || checkins.length === 0) {
        return '';
    }
    return checkins
        .map((c) => {
            // Handle the case where timestamp might be undefined
            if (c.timestamp === undefined) {
                return 'Unknown date';
            }
            return format(new Date(c.timestamp), 'MMM dd, yyyy @ h:mm a');
        })
        .join(', ');
}